'use client';

import appLogger from '@/utils/logger';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRouter } from 'next/navigation';

interface AnalysisState {
  status: 'in_progress' | 'completed' | 'failed';
  startedAt: number;
  completedAt?: number;
  failedAt?: number;
  currentStep: number;
  workspaceId: string;
  backgroundMode?: boolean;
  skippedAt?: number;
  phase?: string;
  error?: string;
}

export function useBackgroundAnalysis() {
  const router = useRouter();
  const [backgroundAnalyses, setBackgroundAnalyses] = useState<Record<string, AnalysisState>>({});

  useEffect(() => {
    // Check for any background analyses on mount and every 5 seconds
    const checkBackgroundAnalyses = () => {
      const analyses: Record<string, AnalysisState> = {};
      
      // Find all AI analysis entries in localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('ai_analysis_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            const workspaceId = key.replace('ai_analysis_', '');
            analyses[workspaceId] = data;
            
            // Check if this analysis just completed in background
            if (data.status === 'completed' && data.backgroundMode && !data.notified) {
              // Mark as notified so we don't show multiple toasts
              localStorage.setItem(key, JSON.stringify({ ...data, notified: true }));
              
              // Show success notification with action
              toast.success('AI analysis complete! Your workflows are ready.', {
                autoClose: 10000, // Show for 10 seconds
                closeOnClick: true,
                onClick: () => router.push('/workflows')
              });
            }
          } catch (e) {
            appLogger.error('Error parsing analysis state:', { data: e });
          }
        }
      }
      
      setBackgroundAnalyses(analyses);
    };

    // Check immediately
    checkBackgroundAnalyses();

    // Check every 5 seconds for updates
    const interval = setInterval(checkBackgroundAnalyses, 5000);

    return () => clearInterval(interval);
  }, [router]);

  // Function to get analysis state for a specific workspace
  const getAnalysisState = (workspaceId: string): AnalysisState | null => {
    return backgroundAnalyses[workspaceId] || null;
  };

  // Function to clear analysis state
  const clearAnalysisState = (workspaceId: string) => {
    localStorage.removeItem(`ai_analysis_${workspaceId}`);
    const newAnalyses = { ...backgroundAnalyses };
    delete newAnalyses[workspaceId];
    setBackgroundAnalyses(newAnalyses);
  };

  return {
    backgroundAnalyses,
    getAnalysisState,
    clearAnalysisState,
  };
}